import React, { useMemo, memo } from "react"
import { Text, HStack, Box } from "@chakra-ui/react"
import { ColumnDef } from "@tanstack/react-table"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useNavigate } from "react-router-dom"
import { PaginatedTable, ServerFiltering, ServerPagination, ExpandableRowConfig } from "@hydrologiq/shared/components/PaginatedTable"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import StatusBadge from "@hydrologiq/shared/asset/status/StatusBadge"
import AssetHydrogenLevel from "@hydrologiq/shared/asset/monitoring/AssetHydrogenLevel"
import { statusToText } from "@hydrologiq/shared/asset/attributes/status"
import LatestActivity from "@hydrologiq/shared/attributes/LatestActivity"
import RunDry from "@hydrologiq/shared/asset/attributes/RunDry"
import AssetLogStatus from "@hydrologiq/shared/asset/history/AssetLogStatus"
import { getEnumKeyByEnumValue } from "@hydrologiq/shared/attributes/enum"
import { useGetFeature, useDepletionEnabled } from "@hydrologiq/shared/providers/helpers/useFeature"
import { useIsSectionEnabled } from "@hydrologiq/shared/providers/SectionFeatures"
import { AssetClass, AssetFilterOptions, TenantProfile } from "@hydrologiq/gql-experience/graphql"
import MultiCurrentOrdersDisplay from "@hydrologiq/shared/components/MultiCurrentOrdersDisplay"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useState, useEffect, useCallback, useRef } from "react"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { useGetProfile } from "@hydrologiq/shared/providers/helpers/useProfile"
import { useStableAlerts } from "../hooks/useStableAlerts"
import { AssetFilters } from "../hooks/usePaginatedSiteAssets"
import AssetLink from "./AssetLink"
import { AlertsPopover } from "../components/alerts"
import Link from "../components/Link"
import DataConnectionIndicator from "../data-connections/DataConnectionIndicator"

enum AssetGroups {
  All = "All",
  FuelProducers = "ALL (Fuel producers)",
  Rental = "ALL (Rental)",
  Generator = "Generator",
  Solar = "Solar",
  Battery = "Battery",
  InverterCharger = "Inverter/Charger",
  FuelStorage = "Fuel storage",
  Distro = "Distro",
  Refueller = "Refueller",
}

/**
 * Wrapper component that stabilises the alerts array reference
 * before passing it to AlertsPopover, preventing unnecessary re-renders
 * when the parent data is refetched with identical content.
 */
const StableAlertsPopover = memo(function StableAlertsPopover({
  alerts,
  triggerLabel,
  context,
}: {
  alerts: NonNullable<gqlTypes.Asset["alerts"]>
  triggerLabel: string
  context: { asset: string }
}) {
  const stableAlerts = useStableAlerts(alerts)
  return (
    <AlertsPopover
      alerts={stableAlerts}
      totalCount={stableAlerts.length}
      triggerLabel={triggerLabel}
      context={context}
    />
  )
})


interface AssetsTableAdvancedProps {
  assets: gqlTypes.Asset[]
  loading?: boolean
  error?: string | null
  onRetry?: () => void
  // Server pagination props
  serverPagination?: ServerPagination
  // Server filtering props
  serverFiltering?: ServerFiltering<AssetFilters>
  // Filter options for dropdowns
  filterOptions?: AssetFilterOptions
  // Optional header actions (like Create Asset button)
  headerActions?: React.ReactNode
  // Callback for when assets are modified (create/update/delete)
  onAssetsChanged?: () => void | Promise<void>
  // Optional configurations
  onAssetUpdate?: (asset: gqlTypes.Asset) => void
  onNewLocation?: (location: gqlTypes.Location) => void
  locations?: gqlTypes.Location[]
  mapsApiKey?: string
  openExternal?: boolean
  exportComponent?: React.ReactNode
  showCustomerColumn?: boolean
  // Expandable rows configuration (uses PaginatedTable built-in expansion)
  expandableRows?: ExpandableRowConfig<gqlTypes.Asset>
  /** Hide the group filter dropdown (used in nested/child tables) */
  hideGroupFilter?: boolean
  /** When true, formats table as a child table (no controls, no header, tree connectors) */
  isChildTable?: boolean
}
// Helper function to format asset class for display. A Component is named by its
// Subtype when given one: "AC Load" or "DC Load" (CONTEXT.md).
export const formatAssetClass = (
  assetClass: AssetClass | string | null | undefined,
  kind?: string | null,
): string => {
  const normalized = assetClass ? (assetClass.toUpperCase() as AssetClass) : assetClass
  if (normalized === AssetClass.Component) {
    const subtype = kind?.toUpperCase()
    if (subtype === "AC") return "AC Load"
    if (subtype === "DC") return "DC Load"
  }
  switch (normalized) {
    case AssetClass.Genset:
      return "Generator"
    case AssetClass.Solar:
      return "Solar"
    case AssetClass.Battery:
      return "Battery"
    case AssetClass.InverterCharger:
      return "Inverter/Charger"
    case AssetClass.Fuel:
      return "Fuel storage"
    case AssetClass.Distro:
      return "Distro"
    case AssetClass.Storage:
      return "Storage"
    case AssetClass.Power:
      return "Power"
    case AssetClass.Monitoring:
      return "Monitoring"
    case AssetClass.Gas:
      return "Gas"
    case AssetClass.Refueller:
      return "Refueller"
    case AssetClass.Electrolyser:
      return "Electrolyser"
    case AssetClass.Hybrid:
      return "Custom"
    default:
      if (!assetClass) return "n/a"
      return String(assetClass)
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
  }
}

// No longer need to compute monitoring_status - it comes from the API
type AssetTableRow = gqlTypes.Asset

/**
 * Reusable column definitions for a streamlined asset modules/children table.
 * No sorting, no filtering — just a simple list of name, type, and monitoring status.
 * Built from the same base column factories used in AssetsTableAdvanced.
 */
// Cache of known asset types by ID to prevent background queries (e.g. GetSystemsList returning class: null)
// from clobbering an already-resolved asset Type in table rows.
const knownAssetClasses = new Map<string, gqlTypes.AssetClass | string>()

export const moduleColumns: ColumnDef<gqlTypes.Asset, any>[] = [
  {
    accessorKey: "name",
    header: "Name",
    size: 240,
    minSize: 180,
    cell: ({ row }) => (
      <HStack gap="0.75rem" alignItems="center" width="100%" minW={0}>
        <DataConnectionIndicator asset={row.original} />
        <Box flex={1} minW={0} overflow="hidden" textOverflow="ellipsis">
          <AssetLink asset={row.original} tooltip={true} truncate={true} />
        </Box>
      </HStack>
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    accessorKey: "class",
    header: "Type",
    size: 130,
    minSize: 110,
    cell: ({ getValue, row }) => {

      // TODO: Change when we fix the core backend response for type/kind/class. 
      // Currently, some API responses return null for class, so we need to cache known values to avoid overwriting them with null or 'other'.
      const assetId = row.original?.id
      const rawValue = (getValue() as gqlTypes.AssetClass | null) ?? (row.original as any)?.class

      const isNonOther = rawValue && String(rawValue).toLowerCase() !== "other"
      if (assetId && isNonOther) {
        knownAssetClasses.set(assetId, rawValue)
      }

      // If this asset already has a resolved type, any incoming null, undefined,
      // or "other" must NOT overwrite the known type.
      const value = (isNonOther ? rawValue : null) ?? (assetId ? knownAssetClasses.get(assetId) : null)
      if (!value) return "n/a"
      const formatted = formatAssetClass(value, row.original?.kind)
      return <Text>{formatted.replace(/\//g, "/\u200B")}</Text>
    },
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: "customer",
    header: "Customer",
    size: 140,
    minSize: 110,
    accessorFn: (row) => row.deployed_customer?.name || row.current_order?.customer?.name || "",
    cell: ({ row }) => {
      const asset = row.original
      const customer = asset.deployed_customer || asset.current_order?.customer
      if (!customer) return <Text color="gray.400">n/a</Text>
      return <Text>{customer.name}</Text>
    },
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: "site",
    header: "Site",
    size: 140,
    minSize: 110,
    accessorFn: (row) => row.deployed_site?.name || row.latest_activity?.site?.name || "",
    cell: ({ row }) => {
      const asset = row.original
      const site = asset.deployed_site || asset.latest_activity?.site
      if (!site) return <Text color="gray.400">n/a</Text>
      return <Text>{site.name}</Text>
    },
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    accessorKey: "status",
    header: "Status",
    size: 110,
    minSize: 95,
    cell: ({ row }) => (
      <StatusBadge asset={row.original} edit={false} />
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
]

function AssetsTableAdvanced({
  assets,
  loading,
  error,
  onRetry,
  serverPagination,
  serverFiltering,
  filterOptions,
  headerActions,
  onAssetsChanged: _onAssetsChanged,
  onAssetUpdate,
  onNewLocation: _onNewLocation,
  locations: _locations,
  mapsApiKey: _mapsApiKey,
  openExternal = false,
  exportComponent,
  showCustomerColumn = true,
  expandableRows,
  hideGroupFilter = false,
  isChildTable = false,
}: AssetsTableAdvancedProps) {
  const route = useRoute()
  const navigate = useNavigate()
  const depletionEnabled = useDepletionEnabled()
  const ordersEnabled = useIsSectionEnabled("orders")
  const isCustomersEnabled = useIsSectionEnabled("customers")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.CURRENT_ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CURRENT_ORDER],
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.HEADINGS.LEVEL]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.LEVEL],
  })

  const profile = useGetProfile()

  // Get initial grouping based on user profile
  const getInitialGrouping = useCallback(() => {
    // Map profile to appropriate ALL grouping
    switch (profile) {
      case TenantProfile.Rental:
        return AssetGroups.Rental
      case TenantProfile.FuelProducer:
        return AssetGroups.FuelProducers
      default:
        // Default to fuel_producer if no profile or unknown profile
        return AssetGroups.FuelProducers
    }
  }, [profile])

  // Group filter state with profile-based initial value
  const defaultGrouping = getInitialGrouping()
  const [grouping, setGrouping] = useState<AssetGroups>(defaultGrouping)
  const previousGroupingRef = useRef<AssetGroups>(getInitialGrouping())

  // Stable function to change server filter
  const changeServerFilter = useCallback(
    (groupingValue: string) => {
      if (!serverFiltering?.onFiltersChange) return

      // Map grouping values to backend-compatible filter values
      let backendGrouping: string | undefined
      let classFilter: string | undefined

      switch (groupingValue) {
        case AssetGroups.All:
          backendGrouping = undefined
          classFilter = undefined
          break
        case AssetGroups.FuelProducers:
          backendGrouping = "Fuel producers"
          classFilter = undefined
          break
        case AssetGroups.Rental:
          backendGrouping = "Rental"
          classFilter = undefined
          break
        case AssetGroups.Generator:
          classFilter = "genset"
          break
        case AssetGroups.Solar:
          classFilter = "solar"
          break
        case AssetGroups.Battery:
          classFilter = "battery"
          break
        case AssetGroups.InverterCharger:
          classFilter = "inverter_charger"
          break
        case AssetGroups.FuelStorage:
          classFilter = "fuel"
          break
        case AssetGroups.Distro:
          classFilter = "distro"
          break
        case AssetGroups.Refueller:
          classFilter = "refueller"
          break
        default:
          backendGrouping = undefined
          classFilter = undefined
          break
      }

      // Preserve existing filters but update grouping and class
      const currentFilters = serverFiltering.filters || {}
      serverFiltering.onFiltersChange({
        ...currentFilters,
        grouping: backendGrouping,
        class: classFilter,
      })
    },
    [serverFiltering?.onFiltersChange]
  )

  // Apply group filter to server filtering using grouping parameter
  useEffect(() => {
    // Only update if grouping actually changed
    if (previousGroupingRef.current !== grouping) {
      changeServerFilter(grouping)
      previousGroupingRef.current = grouping


    }
  }, [grouping, changeServerFilter, serverFiltering])

  // Column factory functions for dynamic generation
  // These extend the base cell renderers (shared with moduleColumns) with filtering/sorting config.
  const createAssetNameColumn = (): ColumnDef<AssetTableRow, any> => ({
    ...moduleColumns[0],
    header: "Asset Name",
    size: 240,
    minSize: 180,
    cell: ({ row }) => {
      const asset = row.original
      return (
        <HStack gap="0.75rem" alignItems="center" width="100%" minW={0}>
          <DataConnectionIndicator asset={asset} />
          <Box flex={1} minW={0} overflow="hidden" textOverflow="ellipsis">
            <AssetLink asset={asset} openExternal={openExternal} tooltip={true} truncate={true} />
          </Box>
        </HStack>
      )
    },
    enableSorting: undefined,
    enableColumnFilter: undefined,
    meta: {
      filterVariant: "select",
    },
  })

  const createTypeColumn = (): ColumnDef<AssetTableRow, any> => ({
    ...moduleColumns[1],
    size: 130,
    minSize: 110,
    enableSorting: undefined,
    enableColumnFilter: undefined,
    meta: {
      filterVariant: "select",
      filterOptions: filterOptions?.classes?.map((cls) => ({ label: formatAssetClass(cls.value) || cls.name, value: cls.value })),
    },
  })

  const createSourceColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "category",
    header: "Source",
    cell: ({ getValue }) => {
      const category = getValue() as string | null
      if (!category) return <Text color="gray.400">n/a</Text>
      return <Text>{category === "hydrogen" ? "Hydrogen" : category === "diesel" ? "Diesel" : category}</Text>
    },
    enableColumnFilter: false,
    size: 95,
    minSize: 80,
  })

  const createSubtypeColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "kind",
    header: "Subtype",
    cell: ({ getValue }) => {
      const kind = getValue() as string | null
      if (!kind || kind === "other") return <Text color="gray.400">n/a</Text>
      switch (kind) {
        case "tube_trailer":
          return <Text>Tube Trailer</Text>
        case "manifold_cylinder_pallet":
          return <Text>Manifold</Text>
        case "dynamic_storage":
          return <Text>Dynamic Storage</Text>
        default:
          return <Text>{kind.replace(/_/g, " ")}</Text>
      }
    },
    enableColumnFilter: false,
    size: 125,
    minSize: 105,
  })

  const createStatusColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "status",
    header: "Status",
    size: 110,
    minSize: 95,
    cell: ({ row }: { row: any }) => {
      const asset = row.original
      return (
        <StatusBadge
          asset={asset}
          editComponent={onAssetUpdate && <AssetLogStatus asset={asset} onUpdate={onAssetUpdate} />}
        />
      )
    },
    meta: {
      filterVariant: "select",
      filterFormatter: (value: any) => statusToText(value as gqlTypes.AssetStatus),
      filterOptions: filterOptions?.statuses,
    },
  })

  const createCurrentOrderColumn = (): ColumnDef<AssetTableRow, any> => ({
    id: "current_order",
    accessorFn: (row) => row.current_order?.name || `No ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}`,
    header: copy[COPY_KEYS.HEADINGS.CURRENT_ORDER],
    cell: ({ row }) => {
      const asset = row.original
      const currentOrders = asset.current_order ? [asset.current_order] : []
      return currentOrders.length > 0 ? (
        <MultiCurrentOrdersDisplay orders={currentOrders} maxVisible={1} />
      ) : (
        <Text>No {copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}</Text>
      )
    },
    enableColumnFilter: false,
    size: 130,
    minSize: 100,
  })

  const createCustomerColumn = (): ColumnDef<AssetTableRow, any> => ({
    id: "customer",
    accessorFn: (row) => row.deployed_customer?.name || row.current_order?.customer?.name || "No customer",
    header: "Customer",
    cell: ({ row }) => {
      const asset = row.original
      const customer = asset.deployed_customer || asset.current_order?.customer

      if (!customer) {
        return <Text>No customer</Text>
      }

      return (
        <Link href={`/customers/${customer.id}`} variant="primary" truncate maxWidth="100%" deLink={!isCustomersEnabled}>
          {customer.name}
        </Link>
      )
    },
    size: 140,
    minSize: 110,
    enableSorting: true,
    meta: {
      filterVariant: "select",
      filterOptions: (filterOptions as any)?.customers?.map((customer: any) => ({
        label: customer.name,
        value: customer.value
      })),
    },
  })

  const createAlertsColumn = (): ColumnDef<AssetTableRow, any> => ({
    id: "alerts",
    header: "Alerts",
    size: 85,
    minSize: 70,
    cell: ({ row }) => {
      const asset = row.original

      const alertsValue = asset.alerts

      if (!alertsValue) {
        return <Text color="gray.400">None</Text>
      }

      return <StableAlertsPopover alerts={alertsValue} triggerLabel={asset.name} context={{ asset: asset.id }} />
    },
    enableColumnFilter: false,
  })

  const createLastKnownLocationColumn = (): ColumnDef<AssetTableRow, any> => ({
    id: "latest_activity",
    accessorFn: (row) => row.deployed_site?.name || row.latest_activity?.site?.name || "No activity",
    header: "Current Site",
    cell: ({ row }) => {
      const asset = row.original
      const deployedSite = asset.deployed_site

      if (deployedSite) {
        return (
          <Link href={`/customers/${asset.deployed_customer?.id}/sites/${deployedSite.id}`} variant="primary" truncate maxWidth="100%" deLink={!isCustomersEnabled}>
            {deployedSite.name}
          </Link>
        )
      }

      return (
        <LatestActivity
          activity={asset.latest_activity as gqlTypes.AssetLatestActivity | null | undefined}
          order={asset.current_order}
          simple
        />
      )
    },
    size: 135,
    minSize: 110,
    enableSorting: false,
    enableColumnFilter: false,
  })

  const createBaseColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "base",
    header: "Depot",
    accessorFn: (row) => row.base?.name || "n/a",
    size: 110,
    minSize: 90,
    meta: {
      filterVariant: "select",
      filterOptions: filterOptions?.bases?.map((base) => ({ label: base.name, value: base.value })),
    },
  })

  const createRunDryColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "run_dry_minutes",
    header: "Asset Depleted in",
    cell: ({ row }) => {
      const asset = row.original
      return asset.run_dry_minutes ? <RunDry asset={asset} /> : <Text color="gray.400">n/a</Text>
    },
    enableColumnFilter: false,
    size: 135,
    minSize: 110,
  })

  const createHydrogenLevelColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "current_level_kg",
    header: copy[COPY_KEYS.HEADINGS.LEVEL],
    cell: ({ row }) => {
      const asset = row.original
      return <AssetHydrogenLevel asset={asset} />
    },
    enableColumnFilter: false,
    size: 95,
    minSize: 80,
  })

  const createCurrentPowerColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "power",
    header: "Current Power (kW)",
    cell: ({ row }) => {
      const asset = row.original
      const powerValue = (asset as any).power
      return powerValue ? <Text>{Number(powerValue).toFixed(1)}</Text> : <Text>n/a</Text>
    },
    enableColumnFilter: false,
    size: 130,
    minSize: 105,
  })

  const createEnergyYesterdayColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "energy",
    header: "Energy Yesterday (kWh)",
    cell: ({ row }) => {
      const asset = row.original
      const energyValue = asset.energy
      return energyValue ? <Text>{Number(energyValue).toFixed(2)}</Text> : <Text>n/a</Text>
    },
    enableColumnFilter: false,
    size: 145,
    minSize: 120,
  })

  const createCO2eYesterdayColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "co2e",
    header: "CO2e Yesterday (kg)",
    cell: ({ row }) => {
      const asset = row.original
      const co2eValue = asset.co2e
      return co2eValue ? <Text>{Number(co2eValue).toFixed(2)}</Text> : <Text>n/a</Text>
    },
    enableColumnFilter: false,
    size: 140,
    minSize: 115,
  })

  const createStateOfChargeColumn = (): ColumnDef<AssetTableRow, any> => ({
    accessorKey: "state_of_charge",
    header: "State of charge",
    cell: ({ row }) => {
      const asset = row.original
      if (asset.kind !== gqlTypes.AssetKind.Battery) {
        return <Text>n/a</Text>
      }
      const socValue = (asset as any).state_of_charge
      return socValue ? <Text>{Number(socValue).toFixed(1)}%</Text> : <Text>n/a</Text>
    },
    enableColumnFilter: false,
    size: 120,
    minSize: 95,
  })

  // const createCreatedColumn = (): ColumnDef<AssetTableRow, any> => ({
  //   accessorKey: "created_at",
  //   header: "Created",
  //   cell: ({ getValue }) => {
  //     const date = getValue() as string
  //     return <Text>{new Date(date).toLocaleDateString()}</Text>
  //   },
  //   enableColumnFilter: false,
  // })

  // Dynamic column generation based on grouping and visibility matrix
  const columns = useMemo<ColumnDef<AssetTableRow, any>[]>(() => {
    const baseColumns: ColumnDef<AssetTableRow, any>[] = []

    // Always include Asset Name
    baseColumns.push(createAssetNameColumn())

    // Only include Type column for "All" view
    if ([AssetGroups.All, AssetGroups.FuelProducers, AssetGroups.Rental].includes(grouping)) {
      baseColumns.push(createTypeColumn())
    }

    // Status column (always shown)
    baseColumns.push(createStatusColumn())

    // Determine whether to show the "Asset Depleted in" column based on profile + grouping
    const isFuelStorage = grouping === AssetGroups.FuelStorage
    const isFuelProducerDefault = grouping === AssetGroups.FuelProducers || grouping === AssetGroups.All
    const showRunDry =
      depletionEnabled && (
        isFuelStorage || // Always show for fuel storage
        (profile === TenantProfile.FuelProducer && isFuelProducerDefault) // Show for fuel producer "ALL" view
      )

    // Group-specific column logic based on visibility matrix
    let additionalColumns: ColumnDef<AssetTableRow, any>[] = []

    switch (grouping) {
      case AssetGroups.All:
        // Show all columns for "All" view
        if (ordersEnabled) {
          additionalColumns.push(createCurrentOrderColumn())
          if (showCustomerColumn) {
            additionalColumns.push(createCustomerColumn())
          }
        }
        additionalColumns.push(createAlertsColumn())
        if (ordersEnabled) {
          additionalColumns.push(createLastKnownLocationColumn())
        }
        if (showRunDry) additionalColumns.push(createRunDryColumn())
        additionalColumns.push(createHydrogenLevelColumn())
        additionalColumns.push(createEnergyYesterdayColumn())
        additionalColumns.push(createCO2eYesterdayColumn())
        additionalColumns.push(createStateOfChargeColumn())
        break

      case AssetGroups.FuelProducers:
        // ALL (Fuel producers) - Hydrogen-based assets
        if (ordersEnabled) {
          additionalColumns.push(createCurrentOrderColumn())
          if (showCustomerColumn) {
            additionalColumns.push(createCustomerColumn())
          }
        }
        additionalColumns.push(createAlertsColumn())
        if (ordersEnabled) {
          additionalColumns.push(createLastKnownLocationColumn())
        }
        if (showRunDry) additionalColumns.push(createRunDryColumn())
        additionalColumns.push(createHydrogenLevelColumn())
        break

      case AssetGroups.Rental:
        // ALL (Rental) - Energy-based assets
        if (ordersEnabled) {
          additionalColumns.push(createCurrentOrderColumn())
          if (showCustomerColumn) {
            additionalColumns.push(createCustomerColumn())
          }
        }
        additionalColumns.push(createAlertsColumn())
        if (ordersEnabled) {
          additionalColumns.push(createLastKnownLocationColumn())
        }
        if (showRunDry) additionalColumns.push(createRunDryColumn())
        additionalColumns.push(createCurrentPowerColumn())
        additionalColumns.push(createEnergyYesterdayColumn())
        additionalColumns.push(createCO2eYesterdayColumn())
        break

      case AssetGroups.FuelStorage:
        // Fuel storage - Hydrogen-based
        additionalColumns.push(createSourceColumn())
        additionalColumns.push(createSubtypeColumn())
        if (ordersEnabled) {
          additionalColumns.push(createCurrentOrderColumn())
          if (showCustomerColumn) {
            additionalColumns.push(createCustomerColumn())
          }
        }
        additionalColumns.push(createAlertsColumn())
        if (ordersEnabled) {
          additionalColumns.push(createLastKnownLocationColumn())
        }
        if (showRunDry) additionalColumns.push(createRunDryColumn())
        additionalColumns.push(createHydrogenLevelColumn())
        break

      case AssetGroups.Generator:
        // Generator - Energy-based
        additionalColumns.push(createSourceColumn())
        if (ordersEnabled) {
          additionalColumns.push(createCurrentOrderColumn())
          if (showCustomerColumn) {
            additionalColumns.push(createCustomerColumn())
          }
        }
        additionalColumns.push(createAlertsColumn())
        if (ordersEnabled) {
          additionalColumns.push(createLastKnownLocationColumn())
        }
        additionalColumns.push(createCurrentPowerColumn())
        additionalColumns.push(createEnergyYesterdayColumn())
        additionalColumns.push(createCO2eYesterdayColumn())
        break

      case AssetGroups.Solar:
      case AssetGroups.InverterCharger:
      case AssetGroups.Distro:
      case AssetGroups.Refueller:
        // Solar / Inverter/Charger / Distro - Energy-based
        if (ordersEnabled) {
          additionalColumns.push(createCurrentOrderColumn())
          if (showCustomerColumn) {
            additionalColumns.push(createCustomerColumn())
          }
        }
        additionalColumns.push(createAlertsColumn())
        if (ordersEnabled) {
          additionalColumns.push(createLastKnownLocationColumn())
        }
        additionalColumns.push(createCurrentPowerColumn())
        additionalColumns.push(createEnergyYesterdayColumn())
        additionalColumns.push(createCO2eYesterdayColumn())
        break

      case AssetGroups.Battery:
        // Battery - Energy-based with state of charge
        if (ordersEnabled) {
          additionalColumns.push(createCurrentOrderColumn())
          if (showCustomerColumn) {
            additionalColumns.push(createCustomerColumn())
          }
        }
        additionalColumns.push(createAlertsColumn())
        if (ordersEnabled) {
          additionalColumns.push(createLastKnownLocationColumn())
        }
        additionalColumns.push(createCurrentPowerColumn())
        additionalColumns.push(createEnergyYesterdayColumn())
        additionalColumns.push(createStateOfChargeColumn())
        break
    }

    // Legacy monitoring status column removed — now indicated inline with asset name
    additionalColumns.push(createBaseColumn())

    return [...baseColumns, ...additionalColumns]
  }, [
    grouping,
    profile,
    route,
    navigate,
    showCustomerColumn,
    depletionEnabled,
    ordersEnabled,
    openExternal,
    onAssetUpdate,
    copy,
    filterOptions,
  ])


  // Enhanced header actions with group filter
  const enhancedHeaderActions = (
    <HStack gap={2}>
      {headerActions}

      {!hideGroupFilter && (
        <HStack gap="0.375rem" alignItems="center">
          <Text fontSize="0.75rem" color="gray.600" whiteSpace="nowrap">
            Showing:
          </Text>
          <NativeSelectRoot size="sm" width="auto">
            <NativeSelectField
              value={grouping}
              onChange={(e) => {
                setGrouping(e.target.value as AssetGroups)
              }}
              items={[
                // Profile-specific ALL options display as simply "ALL"
                { label: "All", value: AssetGroups[getEnumKeyByEnumValue(AssetGroups, defaultGrouping) as keyof typeof AssetGroups] },
                { label: "Generator", value: AssetGroups.Generator },
                { label: "Solar", value: AssetGroups.Solar },
                { label: "Battery", value: AssetGroups.Battery },
                { label: "Inverter/Charger", value: AssetGroups.InverterCharger },
                { label: "Fuel storage", value: AssetGroups.FuelStorage },
                { label: "Distro", value: AssetGroups.Distro },
                { label: "Refueller", value: AssetGroups.Refueller },
              ]}
            />
          </NativeSelectRoot>
        </HStack>
      )}
    </HStack>
  )


  const emptyExpanderColumn: ColumnDef<AssetTableRow, any> = useMemo(
    () => ({
      id: "__expand",
      header: "",
      size: 32,
      minSize: 32,
      cell: () => null,
      enableSorting: false,
      enableColumnFilter: false,
    }),
    []
  )

  const finalColumns = useMemo(
    () => (isChildTable ? [emptyExpanderColumn, ...columns] : columns),
    [isChildTable, emptyExpanderColumn, columns]
  )

  return (
    <PaginatedTable<gqlTypes.Asset, AssetFilters>
      data={assets}
      columns={finalColumns}
      loading={loading}
      error={error}
      onRetry={onRetry}
      serverPagination={isChildTable ? undefined : serverPagination}
      serverFiltering={isChildTable ? undefined : serverFiltering}
      headerActions={isChildTable ? undefined : enhancedHeaderActions}
      emptyState={{
        heading: isChildTable ? "No child assets found" : "No assets",
        text: isChildTable ? "" : "Please create an asset to get started",
      }}
      variant="hydrologiq"
      entityName="assets"
      exportComponent={isChildTable ? undefined : exportComponent}
      expandableRows={isChildTable ? undefined : expandableRows}
      hideControls={isChildTable}
      hideHeader={isChildTable}
      hidePagination={isChildTable}
      isNested={isChildTable}
      getRowStyle={isChildTable ? () => ({ background: "#F8FAFC" }) : undefined}
    />
  )
}

export default AssetsTableAdvanced
